import { Loader2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { AppHeader } from "@/components/app-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useUsage } from "@/lib/api";
import { errorStatus, formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";

const DAY_OPTIONS = [7, 30, 90] as const;

const TH = "text-muted px-4 py-2 text-left text-xs font-medium uppercase tracking-wide";
const TD = "px-4 py-2";
const NUM = "px-4 py-2 text-right tabular-nums";

interface SectionProps {
  title: string;
  empty: boolean;
  children: React.ReactNode;
}

function TableSection({ title, empty, children }: SectionProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent className="px-0 pb-2">
        {empty ? (
          <p className="text-muted px-6 pb-4 text-sm">No calls in this period.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">{children}</table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function UsagePage() {
  const [days, setDays] = useState<number>(30);
  const { data, isLoading, isError, error, refetch, isFetching } = useUsage(days);

  const forbidden = errorStatus(error) === 403;
  const totalCalls = data?.by_person.reduce((sum, p) => sum + p.calls, 0) ?? 0;

  return (
    <div className="bg-background min-h-screen">
      <AppHeader />
      <main className="mx-auto max-w-4xl space-y-6 px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-heading text-primary text-3xl font-bold">Usage</h1>
            <p className="text-muted mt-2 text-sm">How the team uses Marketing OS in Claude.</p>
          </div>
          {!forbidden && (
            <div className="flex items-center gap-1" role="group" aria-label="Period">
              {DAY_OPTIONS.map((option) => (
                <Button
                  key={option}
                  size="sm"
                  variant={option === days ? "default" : "outline"}
                  onClick={() => setDays(option)}
                  aria-pressed={option === days}
                >
                  {option} days
                </Button>
              ))}
              {isFetching && <Loader2 className="text-muted ml-2 h-4 w-4 animate-spin" />}
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="text-muted h-6 w-6 animate-spin" />
          </div>
        ) : forbidden ? (
          <Card>
            <CardContent className="py-10 text-center">
              <p className="text-foreground font-medium">Admins only</p>
              <p className="text-muted mt-2 text-sm">
                Usage reports are visible to wiki admins.{" "}
                <Link to="/home" className="underline">
                  Back to home
                </Link>
              </p>
            </CardContent>
          </Card>
        ) : isError || !data ? (
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted text-sm">
                Could not load usage.{" "}
                <button type="button" onClick={() => void refetch()} className="underline">
                  Try again
                </button>
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card>
              <CardContent className="pt-6">
                <p className="text-muted text-sm">Total calls in the last {data.days} days</p>
                <p className="text-primary mt-1 text-4xl font-bold tabular-nums">
                  {totalCalls.toLocaleString("en-US")}
                </p>
              </CardContent>
            </Card>

            <TableSection title="Calls per person" empty={data.by_person.length === 0}>
              <thead>
                <tr className="border-border border-b">
                  <th className={TH}>Person</th>
                  <th className={TH}>Profile</th>
                  <th className={`${TH} text-right`}>Calls</th>
                  <th className={TH}>Last call</th>
                </tr>
              </thead>
              <tbody className="divide-border divide-y">
                {data.by_person.map((row) => (
                  <tr key={row.user_email}>
                    <td className={TD}>{row.user_email}</td>
                    <td className={`${TD} text-muted`}>{row.profile ?? "unknown"}</td>
                    <td className={NUM}>{row.calls}</td>
                    <td className={`${TD} text-muted`}>{formatDateTime(row.last_call)}</td>
                  </tr>
                ))}
              </tbody>
            </TableSection>

            <TableSection title="Top recipes" empty={data.top_recipes.length === 0}>
              <thead>
                <tr className="border-border border-b">
                  <th className={TH}>Task</th>
                  <th className={`${TH} text-right`}>Calls</th>
                  <th className={`${TH} text-right`}>People</th>
                </tr>
              </thead>
              <tbody className="divide-border divide-y">
                {data.top_recipes.map((row) => (
                  <tr key={row.task}>
                    <td className={TD}>{row.task}</td>
                    <td className={NUM}>{row.calls}</td>
                    <td className={NUM}>{row.people}</td>
                  </tr>
                ))}
              </tbody>
            </TableSection>

            <TableSection
              title={`Calls per tool per week (last ${data.weeks} weeks)`}
              empty={data.by_tool_week.length === 0}
            >
              <thead>
                <tr className="border-border border-b">
                  <th className={TH}>Week of</th>
                  <th className={TH}>Tool</th>
                  <th className={`${TH} text-right`}>Calls</th>
                  <th className={`${TH} text-right`}>Errors</th>
                </tr>
              </thead>
              <tbody className="divide-border divide-y">
                {data.by_tool_week.map((row) => (
                  <tr key={`${row.week}-${row.tool}`}>
                    <td className={`${TD} tabular-nums`}>{row.week}</td>
                    <td className={`${TD} font-mono text-xs`}>{row.tool}</td>
                    <td className={NUM}>{row.calls}</td>
                    <td className={cn(NUM, row.errors > 0 ? "text-destructive" : "text-muted")}>
                      {row.errors}
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableSection>
          </>
        )}
      </main>
    </div>
  );
}
